import { Injectable } from '@angular/core';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import type { StripeConnectInstance, AppearanceOptions, IStripeConnectInitParams, CssFontSource, CustomFontSource } from '@stripe/connect-js';

@Injectable({
  providedIn: 'root'
})
export class StripeConnectService {
  private instance: StripeConnectInstance | null = null;

  /**
   * Initialize Stripe Connect using the official @stripe/connect-js package
   * @param publishableKey - Your Stripe publishable key
   * @param fetchClientSecret - Function that fetches the account session client secret
   * @param appearance - Optional appearance configuration
   */
  async init(
    publishableKey: string,
    fetchClientSecret: () => Promise<string | undefined>,
    appearance?: AppearanceOptions
  ): Promise<StripeConnectInstance> {
    // Validate required parameters
    if (!publishableKey) {
      throw new Error('publishableKey is required');
    }
    if (!fetchClientSecret || typeof fetchClientSecret !== 'function') {
      throw new Error('fetchClientSecret must be a function');
    }

    // Create fetchClientSecret wrapper function
    const fetchClientSecretWrapper = async (): Promise<string> => {
      const secret = await fetchClientSecret();
      if (!secret) {
        throw new Error('Failed to fetch client secret');
      }
      return secret;
    };

    // Configure Poppins font using CssFontSource (Google Fonts)
    // This matches how Poppins is loaded in the application (via Google Fonts CSS)
    // Note: CssFontSource is recommended for Google Fonts as it handles all weights/styles automatically
    // If you prefer CustomFontSource, you would need to provide direct font file URLs (.woff2) for each weight/style
    const poppinsFont: CssFontSource = {
      cssSrc: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
    };

    // Alternative: If you want to use CustomFontSource instead, you would need to provide direct font file URLs
    // Example (commented out - requires multiple entries for different weights/styles):
    // const poppinsFonts: CustomFontSource[] = [
    //   {
    //     family: 'Poppins',
    //     src: 'url(https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrJJfecnFHGPc.woff2) format("woff2")',
    //     weight: '400',
    //     style: 'normal',
    //     display: 'swap'
    //   },
    //   // Add more entries for other weights/styles as needed
    // ];

    // Create initialization params with explicit structure
    // Ensure all properties are properly defined
    const initParams: IStripeConnectInitParams = {
      publishableKey: String(publishableKey),
      fetchClientSecret: fetchClientSecretWrapper,
      fonts: [poppinsFont],
    };

    // Add appearance if provided
    if (appearance) {
      initParams.appearance = appearance;
    } else {
      initParams.appearance = {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#003b71',
          fontFamily: 'Poppins'
        }
      };
    }

    // Validate initParams before passing to Stripe
    if (!initParams) {
      throw new Error('initParams is null or undefined');
    }
    if (!initParams.publishableKey) {
      throw new Error('initParams.publishableKey is missing');
    }
    if (!initParams.fetchClientSecret || typeof initParams.fetchClientSecret !== 'function') {
      throw new Error('initParams.fetchClientSecret is missing or not a function');
    }

    // Validate that loadConnectAndInitialize is available
    if (!loadConnectAndInitialize || typeof loadConnectAndInitialize !== 'function') {
      throw new Error('loadConnectAndInitialize is not available. Make sure @stripe/connect-js is properly installed.');
    }

    // Debug logging (remove in production if needed)
    console.log('Initializing Stripe Connect with params:', {
      publishableKey: initParams.publishableKey ? 'present' : 'missing',
      fetchClientSecret: typeof initParams.fetchClientSecret,
      appearance: initParams.appearance ? 'present' : 'missing',
      loadConnectAndInitialize: typeof loadConnectAndInitialize
    });

    // Load and initialize Connect.js using the new API
    // Note: loadConnectAndInitialize returns synchronously, but the instance methods may be async
    try {
      // Ensure initParams is passed correctly
      if (!initParams) {
        throw new Error('initParams is undefined when calling loadConnectAndInitialize');
      }
      this.instance = loadConnectAndInitialize(initParams);
    } catch (error) {
      console.error('Error calling loadConnectAndInitialize:', error);
      console.error('initParams at error time:', {
        publishableKey: initParams?.publishableKey,
        fetchClientSecret: typeof initParams?.fetchClientSecret,
        appearance: initParams?.appearance
      });
      console.error('initParams type:', typeof initParams);
      console.error('initParams keys:', initParams ? Object.keys(initParams) : 'N/A');
      throw error;
    }

    if (!this.instance) {
      throw new Error('Failed to initialize Stripe Connect instance');
    }

    return this.instance;
  }

  /**
   * Create a one-off Connect instance (e.g. refund overlay with a fresh account session).
   * Does not replace the singleton instance used by the payments page.
   */
  async createEphemeralInstance(
    publishableKey: string,
    clientSecret: string,
    appearance?: AppearanceOptions
  ): Promise<StripeConnectInstance> {
    if (!publishableKey) {
      throw new Error('publishableKey is required');
    }
    if (!clientSecret) {
      throw new Error('clientSecret is required');
    }

    const poppinsFont: CssFontSource = {
      cssSrc: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
    };

    const initParams: IStripeConnectInitParams = {
      publishableKey: String(publishableKey),
      fetchClientSecret: async () => clientSecret,
      fonts: [poppinsFont],
      appearance: appearance ?? {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#003b71',
          fontFamily: 'Poppins'
        }
      },
    };

    const instance = loadConnectAndInitialize(initParams);
    if (!instance) {
      throw new Error('Failed to initialize ephemeral Stripe Connect instance');
    }
    return instance;
  }

  /**
   * Get the current Stripe Connect instance
   */
  getInstance(): StripeConnectInstance | null {
    return this.instance;
  }

  /**
   * Check if instance is initialized
   */
  isInitialized(): boolean {
    return !!this.instance;
  }
}

